import { useState } from 'react';
import {
  Card,
  Empty,
  Modal,
  Select,
  Skeleton,
  Spin,
  Table,
  Tag,
  Typography,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  PERIOD_OPTIONS,
  PLATFORM_LABELS,
  PLATFORM_OPTIONS,
  useDialogMessages,
  useStatsDialogs,
  useStatsOverview,
  type DialogListItem,
  type StatsFilters,
  type StatsPeriod,
  type StatsPlatform,
} from '@entities/statistics';

const { Title, Text, Paragraph } = Typography;

interface Props {
  assistantId: string;
}

/**
 * Вкладка «Статистика» в карточке ассистента (S-2).
 * Счётчики + график диалогов по дням + фильтры (платформа/период) +
 * таблица диалогов (кликабельные строки) + модалка переписки в стиле чата.
 * Дизайн «Графит»: спокойные тона, суммы Title level={3} weight 500.
 */
export function StatisticsTab({ assistantId }: Props) {
  const [filters, setFilters] = useState<StatsFilters>({
    platform: 'all',
    period: 'all',
  });
  const [openDialogId, setOpenDialogId] = useState<string | null>(null);

  const { data: overview, isLoading: overviewLoading } = useStatsOverview(
    assistantId,
    filters,
  );
  const { data: dialogsData, isLoading: dialogsLoading } = useStatsDialogs(
    assistantId,
    filters,
  );

  const setPlatform = (platform: StatsPlatform) =>
    setFilters((f) => ({ ...f, platform }));
  const setPeriod = (period: StatsPeriod) =>
    setFilters((f) => ({ ...f, period }));

  const dialogs = dialogsData?.dialogs ?? [];

  const columns: ColumnsType<DialogListItem> = [
    {
      title: 'Платформа',
      dataIndex: 'platform',
      key: 'platform',
      width: 120,
      render: (p: string) => (
        <Tag bordered={false} style={{ fontSize: 12 }}>
          {PLATFORM_LABELS[p] ?? p}
        </Tag>
      ),
    },
    {
      title: 'Пользователь',
      dataIndex: 'user_name',
      key: 'user_name',
      width: 160,
      render: (name: string | null, row) => (
        <Text style={{ fontSize: 13 }}>{name || row.user_external_id}</Text>
      ),
    },
    {
      title: 'Последний вопрос',
      dataIndex: 'last_question',
      key: 'last_question',
      ellipsis: true,
      render: (q: string | null) => (
        <Text type="secondary" style={{ fontSize: 13 }}>
          {q || '—'}
        </Text>
      ),
    },
    {
      title: 'Сообщений',
      dataIndex: 'messages_count',
      key: 'messages_count',
      width: 110,
      align: 'center',
      render: (n: number) => (
        <Text type="secondary" style={{ fontSize: 13 }}>
          {n}
        </Text>
      ),
    },
    {
      title: 'Последняя активность',
      dataIndex: 'last_message_at',
      key: 'last_message_at',
      width: 170,
      render: (v: string | null, row) => (
        <Text type="secondary" style={{ fontSize: 13 }}>
          {formatDateTime(v ?? row.created_at)}
        </Text>
      ),
    },
  ];

  return (
    <div>
      {/* Счётчики */}
      <div
        style={{
          display: 'grid',
          gap: 16,
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        }}
      >
        <CounterCard
          label="Всего диалогов"
          value={overview?.total_dialogs}
          loading={overviewLoading}
        />
        <CounterCard
          label="Уникальных пользователей"
          value={overview?.unique_users}
          loading={overviewLoading}
        />
        <CounterCard
          label="Диалогов за период"
          value={overview?.dialogs_in_period}
          loading={overviewLoading}
        />
      </div>

      {/* График */}
      <Card style={{ marginTop: 16 }} styles={{ body: { padding: 20 } }}>
        <Text
          type="secondary"
          style={{ fontSize: 12, display: 'block', marginBottom: 12 }}
        >
          Диалоги по дням
        </Text>
        {overviewLoading ? (
          <Skeleton active paragraph={{ rows: 3 }} />
        ) : overview && overview.by_day.length > 0 ? (
          <DialogsChart data={overview.by_day} />
        ) : (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={<Text type="secondary">Нет данных за период</Text>}
          />
        )}
      </Card>

      {/* Фильтры */}
      <div style={{ display: 'flex', gap: 8, marginTop: 24, flexWrap: 'wrap' }}>
        <Select
          value={filters.platform}
          onChange={setPlatform}
          options={PLATFORM_OPTIONS}
          style={{ minWidth: 160 }}
        />
        <Select
          value={filters.period}
          onChange={setPeriod}
          options={PERIOD_OPTIONS}
          style={{ minWidth: 140 }}
        />
      </div>

      {/* Таблица диалогов */}
      <div style={{ marginTop: 16 }}>
        {dialogsLoading ? (
          <Skeleton active paragraph={{ rows: 5 }} />
        ) : dialogs.length === 0 ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              <Text type="secondary">
                Диалогов пока нет — они появятся после общения пользователей
                с ассистентом в боевых каналах.
              </Text>
            }
          />
        ) : (
          <Table
            columns={columns}
            dataSource={dialogs}
            rowKey="id"
            size="middle"
            onRow={(row) => ({
              onClick: () => setOpenDialogId(row.id),
              style: { cursor: 'pointer' },
            })}
            pagination={
              dialogs.length > 20 ? { pageSize: 20, showSizeChanger: false } : false
            }
          />
        )}
      </div>

      <DialogModal
        assistantId={assistantId}
        dialogId={openDialogId}
        onClose={() => setOpenDialogId(null)}
      />
    </div>
  );
}

function CounterCard({
  label,
  value,
  loading,
}: {
  label: string;
  value?: number;
  loading: boolean;
}) {
  return (
    <Card styles={{ body: { padding: 20 } }}>
      <Text type="secondary" style={{ fontSize: 12 }}>
        {label}
      </Text>
      {loading ? (
        <Skeleton active paragraph={false} title={{ width: 60 }} />
      ) : (
        <Title
          level={3}
          style={{ margin: '4px 0 0', fontWeight: 500, color: '#1F2937' }}
        >
          {value ?? 0}
        </Title>
      )}
    </Card>
  );
}

function DialogsChart({ data }: { data: { date: string; count: number }[] }) {
  const chartData = data.map((d) => ({ date: formatDay(d.date), count: d.count }));
  return (
    <div style={{ width: '100%', height: 220 }}>
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={chartData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
          <defs>
            <linearGradient id="rchStatsFill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#1F2937" stopOpacity={0.16} />
              <stop offset="100%" stopColor="#1F2937" stopOpacity={0.02} />
            </linearGradient>
          </defs>
          <CartesianGrid stroke="#F3F4F6" vertical={false} />
          <XAxis
            dataKey="date"
            tick={{ fontSize: 11, fill: '#9CA3AF' }}
            axisLine={{ stroke: '#E5E7EB' }}
            tickLine={false}
          />
          <YAxis
            width={36}
            allowDecimals={false}
            tick={{ fontSize: 11, fill: '#9CA3AF' }}
            axisLine={false}
            tickLine={false}
          />
          <Tooltip
            formatter={(v: number) => [`${v}`, 'Диалогов']}
            contentStyle={{
              fontSize: 12,
              borderRadius: 6,
              border: '1px solid #E5E7EB',
              boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
            }}
            labelStyle={{ color: '#6B7280' }}
            cursor={{ stroke: '#E5E7EB' }}
          />
          <Area
            type="monotone"
            dataKey="count"
            stroke="#1F2937"
            strokeWidth={2}
            fill="url(#rchStatsFill)"
            dot={{ r: 2.5, fill: '#1F2937', strokeWidth: 0 }}
            activeDot={{ r: 4, fill: '#1F2937', strokeWidth: 0 }}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

/** Модалка переписки в стиле чата. */
function DialogModal({
  assistantId,
  dialogId,
  onClose,
}: {
  assistantId: string;
  dialogId: string | null;
  onClose: () => void;
}) {
  const { data, isLoading } = useDialogMessages(assistantId, dialogId);

  return (
    <Modal
      open={!!dialogId}
      onCancel={onClose}
      footer={null}
      width={640}
      title={
        data
          ? `Диалог — ${data.user_name || 'пользователь'}`
          : 'Диалог'
      }
    >
      {isLoading ? (
        <div style={{ textAlign: 'center', padding: 40 }}>
          <Spin />
        </div>
      ) : !data || data.messages.length === 0 ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={<Text type="secondary">Нет сообщений</Text>}
        />
      ) : (
        <div
          style={{
            maxHeight: '60vh',
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
            gap: 10,
            padding: '8px 4px',
          }}
        >
          {data.messages.map((m) => {
            const isUser = m.direction === 'incoming';
            return (
              <div
                key={m.id}
                style={{
                  display: 'flex',
                  justifyContent: isUser ? 'flex-end' : 'flex-start',
                }}
              >
                <div
                  style={{
                    maxWidth: '78%',
                    background: isUser ? '#1F2937' : '#F3F4F6',
                    color: isUser ? '#FFFFFF' : '#1A1A1A',
                    borderRadius: 12,
                    padding: '8px 12px',
                  }}
                >
                  <Paragraph
                    style={{
                      margin: 0,
                      fontSize: 14,
                      whiteSpace: 'pre-wrap',
                      color: isUser ? '#FFFFFF' : '#1A1A1A',
                    }}
                  >
                    {m.content}
                  </Paragraph>
                  <div
                    style={{
                      fontSize: 10,
                      marginTop: 4,
                      textAlign: 'right',
                      color: isUser ? 'rgba(255,255,255,0.6)' : '#9CA3AF',
                    }}
                  >
                    {formatDateTime(m.created_at)}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Modal>
  );
}

function formatDay(iso: string): string {
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit' });
  } catch {
    return iso;
  }
}

function formatDateTime(iso: string): string {
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}
